import * as React from "react";
import { Check, ChevronLeft, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils.js";
import { apiFetch } from "@/lib/api.js";
import { SearchableSelect } from "@/components/SearchableSelect.js";

interface Space {
  id: string; name: string; type: string; parentId: string | null;
}

interface Props {
  spaces: Space[];
  onComplete: () => void;
  defaultCheckedRooms?: string[];
}

const TYPICAL_ROOMS = ["Kitchen", "Living Room", "Dining Room", "Bedroom", "Bathroom", "Garage", "Attic", "Basement", "Office", "Laundry Room", "Pantry", "Closet"];
const DEFAULT_ROOMS = ["Kitchen", "Living Room", "Bedroom", "Bathroom"];
const QUANTITY_ROOMS = ["Bedroom", "Bathroom"];
const ROOM_ICONS: Record<string, string> = {
  kitchen: "🍳", "living room": "🛋️", "dining room": "🍽️", bedroom: "🛏️", bathroom: "🚿",
  garage: "🚗", attic: "📦", basement: "🏚️", office: "💻", "laundry room": "🧺", pantry: "🥫", closet: "👔",
};
const TYPICAL_FLOORS = ["Main Floor", "Basement", "2nd Floor"];
const FLOOR_ICONS: Record<string, string> = { "main floor": "🏠", basement: "🏚️", "2nd floor": "⬆️" };

type Step = "floor-prompt" | "floor-select" | "rooms";

interface FloorRoomState {
  checkedRooms: Set<string>;
  quantities: Record<string, number>;
  extraNames: string;
}

const emptyFloorRoomState = (): FloorRoomState => ({ checkedRooms: new Set(), quantities: {}, extraNames: "" });
const getIcon = (name: string) => ROOM_ICONS[name.toLowerCase()] || ROOM_ICONS[name.toLowerCase().replace(/\s*\d+$/, "")] || "📍";

export function BulkSpacesWizard({ spaces, onComplete, defaultCheckedRooms }: Props) {
  const [step, setStep] = React.useState<Step>("floor-prompt");
  const [saving, setSaving] = React.useState(false);

  // Determine which rooms to pre-check
  const initialRooms = defaultCheckedRooms ?? DEFAULT_ROOMS;

  // Floor selection state
  const [selectedFloors, setSelectedFloors] = React.useState<Set<string>>(new Set());
  const [extraFloorNames, setExtraFloorNames] = React.useState("");
  const [homeParentId, setHomeParentId] = React.useState<string | null>(null);

  // Per-floor room state (key = floor name), or single "_flat" key when skipping floors
  const [floorRooms, setFloorRooms] = React.useState<Record<string, FloorRoomState>>({
    _flat: { checkedRooms: new Set(initialRooms), quantities: {}, extraNames: "" },
  });

  // Flat mode parent (when skipping floors)
  const [flatParentId, setFlatParentId] = React.useState<string | null>(null);

  // Default parents to first home
  const homes = spaces.filter((s) => s.type === "home");
  const defaultHomeId = homes.length > 0 ? homes[0].id : null;
  React.useEffect(() => {
    if (defaultHomeId !== null) {
      setHomeParentId((prev) => prev ?? defaultHomeId);
      setFlatParentId((prev) => prev ?? defaultHomeId);
    }
  }, [defaultHomeId]);

  // Active floor tab
  const [activeFloor, setActiveFloor] = React.useState<string>("");

  const useFloors = step === "rooms" && !floorRooms._flat;

  const allFloorNames = React.useMemo(() => {
    const names = [...selectedFloors];
    extraFloorNames.split("\n").map((l) => l.trim()).filter(Boolean).forEach((n) => {
      if (!names.some((f) => f.toLowerCase() === n.toLowerCase())) names.push(n);
    });
    return names;
  }, [selectedFloors, extraFloorNames]);

  const toggleFloor = (floor: string) => {
    const next = new Set(selectedFloors);
    next.has(floor) ? next.delete(floor) : next.add(floor);
    setSelectedFloors(next);
  };

  const goToRoomsWithFloors = () => {
    const names = allFloorNames;
    if (!names.length) return;
    const state: Record<string, FloorRoomState> = {};
    names.forEach((f) => { state[f] = emptyFloorRoomState(); });
    setFloorRooms(state);
    setActiveFloor(names[0]);
    setStep("rooms");
  };

  const skipFloors = () => {
    setFloorRooms({ _flat: { checkedRooms: new Set(initialRooms), quantities: {}, extraNames: "" } });
    setStep("rooms");
  };

  const toggleRoom = (floor: string, room: string) => {
    setFloorRooms((prev) => {
      const fs = prev[floor];
      const next = new Set(fs.checkedRooms);
      next.has(room) ? next.delete(room) : next.add(room);
      return { ...prev, [floor]: { ...fs, checkedRooms: next } };
    });
  };

  const setQty = (floor: string, room: string, qty: number) => {
    setFloorRooms((prev) => {
      const fs = prev[floor];
      return { ...prev, [floor]: { ...fs, quantities: { ...fs.quantities, [room]: qty } } };
    });
  };

  const setExtra = (floor: string, val: string) => {
    setFloorRooms((prev) => ({ ...prev, [floor]: { ...prev[floor], extraNames: val } }));
  };

  const buildRoomItems = (fs: FloorRoomState) => {
    const items: { name: string; type: string; quantity: number; roomType: string }[] = [];
    fs.checkedRooms.forEach((room) => items.push({ name: room, type: "room", quantity: fs.quantities[room] || 1, roomType: room.toLowerCase() }));
    fs.extraNames.split("\n").map((l) => l.trim()).filter(Boolean).forEach((n) => {
      if (!items.some((i) => i.name.toLowerCase() === n.toLowerCase())) items.push({ name: n, type: "room", quantity: 1, roomType: n.toLowerCase() });
    });
    return items;
  };

  const hasAnyRooms = Object.values(floorRooms).some((fs) => buildRoomItems(fs).length > 0);

  const [error, setError] = React.useState("");

  const handleSubmit = async () => {
    setSaving(true);
    setError("");
    try {
      if (useFloors) {
        // Step 1: create floors
        const floorNames = Object.keys(floorRooms);
        const floorRes = await apiFetch<{ spaces: { id: string; name: string }[] }>("/api/spaces/bulk", {
          method: "POST",
          body: JSON.stringify({ spaces: floorNames.map((n) => ({ name: n, type: "floor" })), parentId: homeParentId }),
        });
        // Step 2: create rooms per floor
        for (const created of floorRes.spaces) {
          const items = buildRoomItems(floorRooms[created.name]);
          if (items.length) {
            await apiFetch("/api/spaces/bulk", { method: "POST", body: JSON.stringify({ spaces: items, parentId: created.id }) });
          }
        }
      } else {
        const items = buildRoomItems(floorRooms._flat);
        if (items.length) {
          await apiFetch("/api/spaces/bulk", { method: "POST", body: JSON.stringify({ spaces: items, parentId: flatParentId }) });
        }
      }
      onComplete();
    } catch (err: any) { setError(err.message || "Failed to create spaces"); }
    setSaving(false);
  };

  // Checkbox component
  const Checkbox = ({ checked, label, icon, onClick }: { checked: boolean; label: string; icon?: string; onClick: () => void }) => (
    <button onClick={onClick} aria-pressed={checked} className={cn("w-full flex items-center gap-2 p-3 rounded-md border text-left text-sm transition-colors", checked ? "bg-amethyst-soft border-primary text-amethyst-ink" : "bg-card border-clay-border text-body hover:border-primary/40")}>
      <div className={cn("w-5 h-5 rounded-sm border-2 flex items-center justify-center shrink-0", checked ? "bg-primary border-primary" : "border-clay-border")}>
        {checked && <Check className="w-3 h-3 text-white" />}
      </div>
      {icon && <span>{icon}</span>}
      <span className="truncate">{label}</span>
    </button>
  );

  const RoomPicker = ({ floor }: { floor: string }) => {
    const fs = floorRooms[floor];
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {TYPICAL_ROOMS.map((room) => (
            <div key={room}>
              <Checkbox checked={fs.checkedRooms.has(room)} label={room} icon={getIcon(room)} onClick={() => toggleRoom(floor, room)} />
              {fs.checkedRooms.has(room) && QUANTITY_ROOMS.includes(room) && (
                <div className="flex items-center gap-2 mt-1 ml-2">
                  <span className="text-[0.8125rem] text-faint">Qty:</span>
                  <input type="number" min={1} max={10} value={fs.quantities[room] || 1} onChange={(e) => setQty(floor, room, Number(e.target.value))} className="w-16 px-2 py-1 rounded-sm border border-clay-border text-[0.8125rem] text-body tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
                </div>
              )}
            </div>
          ))}
        </div>
        <div>
          <label className="text-sm font-medium text-body mb-1 block">Additional spaces (one per line)</label>
          <textarea value={fs.extraNames} onChange={(e) => setExtra(floor, e.target.value)} placeholder="Sunroom&#10;Wine Cellar&#10;Craft Nook" rows={3} className="w-full px-3 py-2.5 rounded-sm border border-clay-border text-sm text-body placeholder:text-faint resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
        </div>
      </div>
    );
  };

  // --- STEP: floor-prompt ---
  if (step === "floor-prompt") {
    return (
      <div className="space-y-4 text-center py-4">
        <p className="text-sm text-body">Do you want to set up more than one floor?</p>
        <div className="flex justify-center gap-3">
          <button onClick={() => setStep("floor-select")} className="px-6 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors">Yes, add floors</button>
          <button onClick={skipFloors} className="px-6 py-2.5 rounded-md bg-amethyst-soft text-amethyst-ink text-sm font-medium hover:bg-amethyst-soft/70 transition-colors">Skip</button>
        </div>
      </div>
    );
  }

  // --- STEP: floor-select ---
  if (step === "floor-select") {
    return (
      <div className="space-y-4">
        <button onClick={() => setStep("floor-prompt")} className="flex items-center gap-1 text-sm text-faint hover:text-primary transition-colors">
          <ChevronLeft className="w-4 h-4" />Back
        </button>
        <p className="text-sm text-faint">Select or add the floors in your home.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {TYPICAL_FLOORS.map((floor) => (
            <Checkbox key={floor} checked={selectedFloors.has(floor)} label={floor} icon={FLOOR_ICONS[floor.toLowerCase()] || "🏠"} onClick={() => toggleFloor(floor)} />
          ))}
        </div>
        <div>
          <label className="text-sm font-medium text-body mb-1 block">Additional floors (one per line)</label>
          <textarea value={extraFloorNames} onChange={(e) => setExtraFloorNames(e.target.value)} placeholder="3rd Floor&#10;Loft" rows={2} className="w-full px-3 py-2.5 rounded-sm border border-clay-border text-sm text-body placeholder:text-faint resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
        </div>
        {homes.length > 0 && (
          <div>
            <label className="text-sm font-medium text-body mb-1 block">Which home?</label>
            <SearchableSelect spaces={homes} value={homeParentId} onChange={setHomeParentId} placeholder="Select home" />
          </div>
        )}
        <button onClick={goToRoomsWithFloors} disabled={allFloorNames.length === 0} className="w-full py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed">
          Next — Add rooms to floors
        </button>
      </div>
    );
  }

  // --- STEP: rooms ---
  const floorKeys = Object.keys(floorRooms);
  const isFlat = floorKeys.length === 1 && floorKeys[0] === "_flat";

  return (
    <div className="space-y-4">
      <button onClick={() => setStep(isFlat ? "floor-prompt" : "floor-select")} className="flex items-center gap-1 text-sm text-faint hover:text-primary transition-colors">
        <ChevronLeft className="w-4 h-4" />Back
      </button>

      {isFlat ? (
        <>
          <p className="text-sm text-faint">Select rooms to add, then create them all at once.</p>
          <RoomPicker floor="_flat" />
          <div>
            <label className="text-sm font-medium text-body mb-1 block">Parent location</label>
            <SearchableSelect spaces={spaces} value={flatParentId} onChange={setFlatParentId} placeholder="Select parent (e.g. a floor or home)" />
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-faint">Add rooms to each floor.</p>
          {/* Floor tabs */}
          <div className="flex gap-1 overflow-x-auto pb-1">
            {floorKeys.map((f) => (
              <button key={f} onClick={() => setActiveFloor(f)} className={cn("px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors", activeFloor === f ? "bg-primary text-white" : "bg-clay-muted text-body hover:bg-amethyst-soft")}>
                {FLOOR_ICONS[f.toLowerCase()] || "🏠"} {f}
              </button>
            ))}
          </div>
          {activeFloor && floorRooms[activeFloor] && <RoomPicker floor={activeFloor} />}
        </>
      )}

      {error && <p className="text-[0.8125rem] text-error-text">{error}</p>}
      <button onClick={handleSubmit} disabled={saving || !hasAnyRooms} className="w-full py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Create Spaces"}
      </button>
    </div>
  );
}
